import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CallController from '../API/CallController';
import CampaignForm from './CampaignForm';
import ConfirmModal from './ConfirmModal';
import '../scss/ManageCampaigns.scss';

interface ProductWithIdDto {
  id: number;
  name: string;
  description: string;
  price: number;
  categoryId: number;
  discountedPrice?: number;
  priceUnit: string;
}

interface CampaignDto {
  id: number;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  products: ProductWithIdDto[];
}

const ManageCampaigns = () => {
  const [campaigns, setCampaigns] = useState<CampaignDto[]>([]);
  const [editingCampaign, setEditingCampaign] = useState<CampaignDto | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<CampaignDto | null>(null);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(CallController.GetAllCampaigns);
      setCampaigns(res.data);
      console.log('Hämtade kampanjer:', res.data);
    } catch (err) {
      console.error('Kunde inte hämta kampanjer:', err);
    }
  };

  const handleDeleteClick = (campaign: CampaignDto) => {
    setCampaignToDelete(campaign);
  };

  const confirmDelete = async () => {
    if (!campaignToDelete) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Du måste logga in för att kunna ta bort kampanjer.');
      return;
    }

    try {
      await axios.delete(CallController.DeleteCampaignById(campaignToDelete.id), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCampaignToDelete(null);
      fetchCampaigns();
    } catch (err) {
      console.error('Kunde inte ta bort kampanj:', err);
    }
  };


  const handleEdit = (campaign: CampaignDto) => {
    console.log('Redigerar kampanj:', campaign);
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const cancelDelete = () => {
    setCampaignToDelete(null);
  };


  // Prepare to create a new campaign by clearing the editing campaign and showing the form
  const handleCreateNew = () => {
    setEditingCampaign(null);
    setShowForm(true); //show the editing form
  };

  // Handle successful form submission by hiding the form and refreshing the campaign list
  const handleFormSuccess = () => {
    setShowForm(false);
    fetchCampaigns();
  };

  const isActive = (start: string, end: string) => {
    const now = new Date();
    return new Date(start) <= now && now <= new Date(end);
  };

  return (
    <div className="campaigns-container">
      <h2>Hantera kampanjer</h2>
      {!showForm && (
        <button className="admin-button" onClick={handleCreateNew}>+ Lägg till ny kampanj</button>
      )}

      {showForm ? (
        <CampaignForm
          campaign={editingCampaign}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowForm(false)}
        />
      ) : (
        <div className="campaign-list">
          {campaigns.map(c => (
            <div key={c.id} className="campaign-card">
              <h3>{c.title}</h3>
              <p>{c.description}</p>
              <p><strong>Period:</strong> {c.startDate.slice(0, 10)} – {c.endDate.slice(0, 10)}</p>
              <p><strong>Status:</strong> {isActive(c.startDate, c.endDate) ? 'Aktiv' : 'Inaktiv'}</p>
              <p><strong>Produkter:</strong></p>
              <ul>
                {c.products.map((p) => (
                  <li key={p.id}>
                    {p.name} {' '}
                    {p.discountedPrice && p.discountedPrice < p.price ? (
                      <>
                        <span className="original-price">{p.price} {p.priceUnit}</span>{' '}
                        <span className="discounted-price">{p.discountedPrice} {p.priceUnit}</span>
                      </>
                    ) : (
                      <span>{p.price} {p.priceUnit}</span>
                    )}
                  </li>
                ))}
              </ul>

              <div className="campaign-actions">
                <button onClick={() => handleEdit(c)}>Redigera</button>
                <button className="danger" onClick={() => handleDeleteClick(c)}>Ta bort</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {campaignToDelete && (
        <ConfirmModal
          message={`Är du säker på att du vill ta bort kampanjen "${campaignToDelete.title}"?`}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </div>
  );
};

export default ManageCampaigns;
